"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EditorContextPopup = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const BasicButton_1 = require("@jsonjoy.com/ui/lib/2-inline-block/BasicButton");
const ContextMenu_1 = require("@jsonjoy.com/ui/lib/4-card/ContextMenu");
const context_1 = require("@jsonjoy.com/ui/lib/styles/context");
const MoveToViewport_1 = require("@jsonjoy.com/ui/lib/utils/popup/MoveToViewport");
const context_2 = require("@jsonjoy.com/ui/lib/utils/popup/context");
const ScrollArea = tslib_1.__importStar(require("@jsonjoy.com/ui/lib/4-card/ScrollArea"));
const VIEWPORT_MARGIN = 16;
const CONTEXT_PANE_BORDER = 2;
const useElementHeight = (element) => {
    const [height, setHeight] = React.useState(null);
    React.useLayoutEffect(() => {
        if (!element) {
            setHeight(null);
            return;
        }
        const updateHeight = () => {
            const nextHeight = Math.ceil(element.getBoundingClientRect().height);
            setHeight((currentHeight) => (currentHeight === nextHeight ? currentHeight : nextHeight));
        };
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [element]);
    return height;
};
const titleClass = (0, nano_theme_1.rule)({
    d: 'flex',
    jc: 'space-between',
    fz: '12px',
    lh: '1.45',
    pd: '16px',
    fls: '0 0 auto',
    bdrad: '8px 8px 0 0',
    backdropFilter: 'blur(10px)',
});
const titleGroupClass = (0, nano_theme_1.rule)({
    d: 'flex',
    fld: 'column',
});
const footerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    jc: 'flex-end',
    gap: '8px',
    pd: '16px',
    fls: '0 0 auto',
    bdrad: '0 0 8px 8px',
    backdropFilter: 'blur(10px)',
});
const contentClass = (0, nano_theme_1.rule)({
    alignSelf: 'flex-start',
    w: '100%',
    bxz: 'border-box',
    d: 'flex',
    fld: 'column',
    gap: '12px',
    pd: '16px',
});
const EditorContextPopup = ({ title, subtitle, headerRight, noMargin, children, onCancel, onApply, applyDisabled, cancelLabel = 'Cancel', applyLabel = 'Apply', minWidth, maxWidth, }) => {
    const styles = (0, context_1.useStyles)();
    const anchor = (0, context_2.useAnchorPoint)();
    const [headerElement, setHeaderElement] = React.useState(null);
    const [footerElement, setFooterElement] = React.useState(null);
    const [contentElement, setContentElement] = React.useState(null);
    const headerHeight = useElementHeight(headerElement);
    const footerHeight = useElementHeight(footerElement);
    const viewportWidth = typeof window === 'object' ? window.innerWidth : 0;
    const viewportHeight = typeof window === 'object' ? window.innerHeight : 0;
    const safeMinWidth = minWidth !== undefined && viewportWidth
        ? Math.min(minWidth, Math.max(0, viewportWidth - VIEWPORT_MARGIN))
        : minWidth;
    const hasHeader = !!title || !!subtitle || !!headerRight;
    const hasFooter = !!onApply || !!onCancel;
    const contentHeight = useElementHeight(contentElement);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
    }, []);
    const handleCancel = React.useCallback((event) => {
        event.preventDefault();
        onCancel?.();
    }, [onCancel]);
    const handleApply = React.useCallback((event) => {
        event.preventDefault();
        onApply?.();
    }, [onApply]);
    const header = hasHeader && (React.createElement("div", { ref: setHeaderElement, className: titleClass, style: {
            borderBottom: `1px solid ${styles.g(0, 0.06)}`,
            background: styles.light ? 'rgba(255,255,255,0.7)' : styles.g(1, 0.2),
            color: styles.g(0.24),
        } },
        React.createElement("div", { className: titleGroupClass },
            React.createElement("strong", { style: { display: 'block', marginBottom: 4, color: styles.g(0.12) } }, title),
            subtitle),
        !!headerRight && React.createElement("div", { className: titleGroupClass }, headerRight)));
    const footer = hasFooter && (React.createElement("div", { ref: setFooterElement, className: footerClass, style: {
            borderTop: `1px solid ${styles.g(0, 0.06)}`,
            background: styles.light ? 'rgba(255,255,255,0.7)' : styles.g(1, 0.2),
        } },
        React.createElement(BasicButton_1.BasicButton, { type: "button", width: 'auto', height: 32, 
            // compact
            border: true, onMouseDown: preventMouseDown, onClick: handleCancel }, cancelLabel),
        React.createElement(BasicButton_1.BasicButton, { type: "button", width: 'auto', height: 32, 
            // compact
            border: true, disabled: applyDisabled, onMouseDown: preventMouseDown, onClick: handleApply }, applyLabel)));
    const measuredHeaderHeight = headerHeight ?? 0;
    const measuredFooterHeight = footerHeight ?? 0;
    const safeHeight = Math.max(0, (anchor?.maxHeight() ?? Math.max(0, viewportHeight - 8)) - CONTEXT_PANE_BORDER);
    const availableContentHeight = Math.max(0, safeHeight - measuredHeaderHeight - measuredFooterHeight);
    const contentViewportHeight = contentHeight === null ? availableContentHeight : Math.min(contentHeight, availableContentHeight);
    const scrollHeight = contentHeight === null
        ? safeHeight
        : Math.min(safeHeight, contentViewportHeight + measuredHeaderHeight + measuredFooterHeight);
    const viewportMaxWidth = viewportWidth ? viewportWidth - VIEWPORT_MARGIN : undefined;
    const resolvedMaxWidth = maxWidth !== undefined && viewportMaxWidth !== undefined
        ? Math.min(maxWidth, viewportMaxWidth)
        : (maxWidth ?? viewportMaxWidth);
    return (React.createElement(MoveToViewport_1.MoveToViewport, { vertical: true },
        React.createElement(ContextMenu_1.ContextPane, { minWidth: safeMinWidth, style: {
                maxWidth: resolvedMaxWidth,
                width: maxWidth !== undefined ? resolvedMaxWidth : undefined,
            } },
            React.createElement(ScrollArea.ScrollArea, { shadow: true, railWidth: 4, style: { height: scrollHeight } },
                header ? React.createElement(ScrollArea.Header, null, header) : null,
                React.createElement(ScrollArea.Viewport, null,
                    React.createElement("div", { ref: setContentElement, className: contentClass, style: {
                            ...(noMargin ? { padding: 0 } : undefined),
                        } }, children)),
                React.createElement(ScrollArea.ScrollRail, null),
                footer ? React.createElement(ScrollArea.Footer, null, footer) : null))));
};
exports.EditorContextPopup = EditorContextPopup;
